import moment from "moment";
import React from "react";
import { useTranslation } from "react-i18next";
import editIcon from "../../../assets/images/icons/edit_white.svg";
import { GeneralButton, Loader, NoResult } from "../../../components";
import { EndPoints } from "../../../Constants/endpoints";
import { useGetData } from "../../../hooks";
import {
  GetSchedule,
  IDays,
  IScheduleItem,
} from "../../../interface/GeneralResponse";
import { mapDayIdToName } from "../../../utils/utils";

interface IShiftState {
  [DayId: number]: {
    shifts: IScheduleItem[];
    isActive: boolean;
  };
}

const ScheduleList: React.FC = () => {
  const { t, i18n } = useTranslation();

  const { data, isPending } = useGetData<GetSchedule>(EndPoints.GetSchedule);
  const scheduleData = data?.Data;
  const scheduleDataAPI = scheduleData?.Schedule;

  const { data: dayData, isPending: isLoading } = useGetData<IDays[]>(
    EndPoints.Days,
  );
  const daysData = dayData?.Data || [];

  moment.locale(i18n.language);

  const getShifts = (): IShiftState => {
    if (!scheduleDataAPI || daysData.length === 0) {
      return {};
    }

    const newShifts: IShiftState = {};

    scheduleDataAPI.forEach((daySchedule) => {
      const formattedShifts: IScheduleItem[] = daySchedule.ScheduleDetails.map(
        (detail) => ({
          DayId: daySchedule.DayId,
          From: detail.FromTime,
          To: detail.ToTime,
          IsActive: daySchedule.IsActive,
        }),
      );

      if (!newShifts[daySchedule.DayId]) {
        newShifts[daySchedule.DayId] = {
          shifts: formattedShifts,
          isActive: daySchedule.IsActive,
        };
      } else {
        newShifts[daySchedule.DayId].shifts.push(...formattedShifts);
      }
    });

    daysData.forEach((day) => {
      if (!newShifts[day.Id]) {
        newShifts[day.Id] = {
          shifts: [],
          isActive: false,
        };
      }
    });

    return newShifts;
  };

  const shifts = getShifts();

  const formatTime = (time: string): string => {
    return time.length > 5
      ? moment(time.slice(0, -3), "hh:mm").format("hh:mm A")
      : moment(time, "hh:mm").format("hh:mm A");
  };

  return (
    <div className="flex h-full w-full flex-col">
      <div className="flex items-center justify-between gap-2">
        <p className="font-bold">{t("clinic_schedule")}</p>
        {scheduleDataAPI && scheduleDataAPI.length !== 0 && (
          <GeneralButton
            color="yellow"
            icon={editIcon}
            title={t("edit")}
            className="!px-3 !py-2"
            route="/profile/clinic_schedule/add"
          />
        )}
      </div>
      {scheduleData?.InspectionDuration && (
        <div className="flex flex-col">
          <p className="text-body-text text-sm">{t("session_duration")}</p>
          <p>
            {scheduleData?.InspectionDuration} {t("min")}
          </p>
        </div>
      )}

      <div className="mt-4 max-h-[50vh] overflow-y-auto">
        {isPending || isLoading ? (
          <Loader />
        ) : (
          <>
            {scheduleDataAPI && scheduleDataAPI.length === 0 ? (
              <div className="flex w-full flex-col items-center justify-center gap-4 rounded-lg bg-InputFieldColor py-11">
                <NoResult isWithLogo />
                <GeneralButton
                  type="button"
                  title={t("add_your_clinic_schedule")}
                  route="/profile/clinic_schedule/add"
                />
              </div>
            ) : (
              Object.entries(shifts).map(([dayId, dayShifts]) =>
                dayShifts.shifts.length > 0 ? (
                  <div
                    key={dayId}
                    className="secondary_warper mb-2 flex justify-between gap-2"
                  >
                    <h6 className="text-primary-textFont text-lg font-normal">
                      {mapDayIdToName(parseInt(dayId, 10), daysData)}
                    </h6>
                    <div className="flex flex-col gap-2">
                      {dayShifts.shifts.map(
                        (shift: IScheduleItem, index: number) => (
                          <div
                            key={`${dayId}-${index}`}
                            className="flex items-center justify-between"
                          >
                            <time className="text-primary-textFont">
                              {formatTime(shift.From)} - {formatTime(shift.To)}
                            </time>
                          </div>
                        ),
                      )}
                    </div>
                  </div>
                ) : null,
              )
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ScheduleList;
